import type { FormatterFn } from './types';

// ---------------------------------------------------------------------------
// Masking formatters
// ---------------------------------------------------------------------------

export const maskSsn: FormatterFn = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

/**
 * Progressive US phone mask: XXX-XXX-XXXX (as-you-type).
 * Matches legacy `phoneFormat` in `utils/global.tsx`. Caps at 10 digits.
 * Full value is 12 chars → Yup `.min(12, 'Must be 10 digits')` for domestic numbers.
 */
export const maskPhone: FormatterFn = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const maskTaxId: FormatterFn = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

export const maskDigits = (maxLength: number): FormatterFn => (value) =>
  value.replace(/\D/g, '').slice(0, maxLength);

/** Digits only, max 20 — opt-in when a form needs digit-only postal input. */
export const maskPostalCode = maskDigits(20);

/** Digits only, max 5 — US ZIP override via `when.formatter` in address blocks. */
export const maskZipCode = maskDigits(5);

// ---------------------------------------------------------------------------
// Numeric formatters
// ---------------------------------------------------------------------------

export const insertCommas: FormatterFn = (value) => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const [integer, decimal] = cleaned.split('.');
  const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimal !== undefined ? `${formatted}.${decimal}` : formatted;
};

export const stripCommas: FormatterFn = (value) => {
  return value.replace(/,/g, '');
};

export const limitDecimalPlaces = (places: number): FormatterFn => {
  return (value) => {
    const dotIdx = value.indexOf('.');
    if (dotIdx === -1) return value;
    return value.slice(0, dotIdx + 1 + places);
  };
};

export const stripNonDigits: FormatterFn = (value) => {
  return value.replace(/[^0-9]/g, '');
};

export const numericWithDashes: FormatterFn = (value) => {
  return value.replace(/[^0-9-]/g, '');
};

/**
 * No-op formatter — use as `falseFormatter` in conditional `when.formatter` branches
 * when the alternate country/mode must not mask input (legacy plain handleChange).
 * Always import from here; NEVER declare identity formatters inside block files.
 */
export const identity: FormatterFn = (value) => value;

// ---------------------------------------------------------------------------
// Text formatters
// ---------------------------------------------------------------------------

/** "NEW YORK" → "New York", "los angeles" → "Los Angeles" */
export function capitalizeWords(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const uppercaseOnly: FormatterFn = (value) => {
  return value.toUpperCase();
};

export const trimExtraSpaces: FormatterFn = (value) => {
  return value.replace(/ {2,}/g, ' ');
};

export const noLeadingSpaces: FormatterFn = (value) => {
  return value.replace(/^\s+/, '');
};

/** "john" → "John", "JOHN" → "John" */
export const capitalizeFirst: FormatterFn = (value) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

// ---------------------------------------------------------------------------
// Whole-number formatters
// ---------------------------------------------------------------------------

export const wholeNumberOnly: FormatterFn = (value) => {
  return value.replace(/\D/g, '');
};

export const wholeNumberWithCommas: FormatterFn = (value) => {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// ---------------------------------------------------------------------------
// Submit-time transform utilities (used in entity-forms/transforms)
// ---------------------------------------------------------------------------

export function stripDashes(str: string): string {
  return str.replace(/-/g, '');
}

export function trimmedToNullable(v: string | undefined | null): string | null {
  if (!v) return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Formats a 7+ digit phone number as XXX-XXX-XXXX. Returns original value for shorter inputs. */
export function phoneFormatWithDash(value: string): string {
  if (!value) return '';
  const n = value.replace(/[^\d]/g, '');
  if (n.length < 7) return value;
  return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6, 10)}`;
}

/** Formats a 9-digit SSN as XXX-XX-XXXX. Returns original value for non-9-digit inputs. */
export function ssnWithDash(value: string): string {
  if (!value) return '';
  const n = value.replace(/[^\d]/g, '');
  if (n.length !== 9) return value;
  return `${n.slice(0, 3)}-${n.slice(3, 5)}-${n.slice(5, 9)}`;
}

// ---------------------------------------------------------------------------
// Composition utility
// ---------------------------------------------------------------------------

export const composeFormatters = (...fns: FormatterFn[]): FormatterFn => {
  return (value) => fns.reduce((v, fn) => fn(v), value);
};

// ---------------------------------------------------------------------------
// Pre-composed formatters (shared across domain primitives)
// ---------------------------------------------------------------------------

/** Strips non-digits then inserts thousand-separator commas. Used by shares, limits, quantities. */
export const commaFormatter = composeFormatters(stripNonDigits, insertCommas);
